import { Translatable } from '@/types'
import React, { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface RenderSafeHtmlProps {
  content: string
  className?: string
  stickyFirstImage?: boolean
}

const RenderSafeHtml: React.FC<RenderSafeHtmlProps> = ({
  content,
  className,
  stickyFirstImage = false,
}) => {
  const [processedContent, setProcessedContent] = useState({
    stickyImage: null as string | null,
    remainingHtml: content,
  })

  useEffect(() => {
    if (!stickyFirstImage || !content) return

    const parser = new DOMParser()
    const doc = parser.parseFromString(content, 'text/html')

    // Find the first image regardless of parent container
    const firstImg = doc.querySelector('img')

    if (firstImg) {
      // Save the image HTML before removing it
      const stickyImgHtml = firstImg.outerHTML

      // Get the parent element that contains the image
      const parent = firstImg.parentElement

      if (parent) {
        // If the parent only contains this image, remove the whole parent
        if (parent.childNodes.length === 1) {
          if (parent.parentNode) {
            parent.parentNode.removeChild(parent)
          }
        } else {
          // Otherwise just remove the image itself
          parent.removeChild(firstImg)
        }

        // Get the remaining HTML after the removal
        const remainingHtml = doc.body.innerHTML

        setProcessedContent({
          stickyImage: stickyImgHtml,
          remainingHtml: remainingHtml,
        })
      }
    }
  }, [content, stickyFirstImage])

  if (!stickyFirstImage) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: content }} />
  }

  return (
    <div className={cn('relative')}>
      {processedContent.stickyImage && (
        <div
          className="sticky top-0 z-10 mx-auto w-full bg-transparent pt-2"
          style={{ position: 'sticky', top: 0 }}
        >
          <div
            className="[&_img]:mt-0 [&_img]:w-full [&_img]:rounded-3xl [&_img]:object-contain"
            dangerouslySetInnerHTML={{ __html: processedContent.stickyImage }}
          />
        </div>
      )}

      <div
        className={cn(
          'relative z-20 rounded-t-3xl bg-background/85 px-10 pb-10 pt-8 backdrop-blur-sm',
          className
        )}
        dangerouslySetInnerHTML={{ __html: processedContent.remainingHtml }}
      />
    </div>
  )
}

export default RenderSafeHtml
